import {
  Address,
  ChainPublicClientService,
  Percent,
  PercentFromBigInt,
  ProtocolKeyService,
} from "@silo/types";
import { Schema } from "@effect/schema";
import { Effect, pipe } from "effect";
import { getContract } from "viem";
import { siloLensAbi } from "@silo/contract-abi-types";
import { siloLensAddress } from "@silo/contract-addresses";
import { ContractsClientError } from "../contracts-client/types";
import { settledOrZero } from "../utils/settle-reverts";

export interface SiloUserState {
  userLtv: Percent;
  userMaxLtv: Percent;
  userLiquidationThreshold: Percent;
}

interface GetSiloUserStateParams {
  marketAddress: Address;
  user: Address;
}

export function getSiloUserState({
  marketAddress,
  user,
}: GetSiloUserStateParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const { publicClient } = yield* ChainPublicClientService;

    const siloLensContract = getContract({
      address: siloLensAddress[protocolKey],
      abi: siloLensAbi,
      client: publicClient,
    });

    const raw = yield* Effect.tryPromise({
      try: async () => {
        /**
         * These three reads all need the collateral's oracle price. When a
         * collateral asset's oracle is unavailable (e.g. removed → reverts with
         * FallbackPriceProviderNotSet), all three revert together. We intentionally
         * do NOT use `requireAnySettled` here: an all-revert is a legitimate state
         * for an unpriced position, not a systemic multicall failure, so we map
         * each result with `settledOrZero` (which still re-throws genuine
         * transport/decoding errors) and degrade to a 0 LTV/max-LTV/LT.
         */
        const results = await Promise.allSettled([
          siloLensContract.read.getUserLTV([marketAddress, user]),
          siloLensContract.read.getUserMaximumLTV([marketAddress, user]),
          siloLensContract.read.getUserLiquidationThreshold([
            marketAddress,
            user,
          ]),
        ]);

        const [userLtv, userMaxLtv, userLiquidationThreshold] =
          results.map(settledOrZero);

        return { userLtv, userMaxLtv, userLiquidationThreshold };
      },
      catch: (cause) =>
        new ContractsClientError({ cause, meta: { marketAddress, user } }),
    });

    const decoded = yield* pipe(raw, Schema.decode(SiloUserStateEncoded));

    const siloUserState: SiloUserState = decoded;

    return siloUserState;
  });
}

const SiloUserStateEncoded = Schema.Struct({
  userLtv: PercentFromBigInt,
  userMaxLtv: PercentFromBigInt,
  userLiquidationThreshold: PercentFromBigInt,
}).annotations({ identifier: "SiloUserStateEncoded" });
